<template>
  <nav class="app-sidebar"
       :class="{
         'collapsed': sidebarCollapsed,
         'theme-skewed': isSkewedTheme
       }"
       role="navigation"
       aria-label="Hauptnavigation">
    <div class="app-sidebar-container">
      <!-- Logo Container -->
      <div class="logo-section">
        <div class="logo-wrapper">
          <img :src="logoUrl" alt="Logo" class="logo-image">
        </div>
      </div>

      <!-- Navigation Content -->
      <div class="navigation-content scroll-y scroll-on-hover">
        <div class="nav-sections">

          <!-- sections -->
          <div class="nav-section"
               v-if="navigationSections && navigationSections.length > 0"
               v-for="section in navigationSections"
               :key="section.id">

            <!-- Wenn collapsed, zeige nur die Items ohne Header -->
            <template v-if="sidebarCollapsed">
              <BPopover
                v-for="item in section.items"
                :key="item.id"
                placement="right"
                triggers="hover"
                :delay="{ show: 0, hide: 0 }">
                <template #target>
                  <BButton class="nav-item"
                      :disabled="item.disabled"
                      :aria-disabled="item.disabled ? 'true' : 'false'"
                      :class="{ active: isActiveRoute(item.route) }"
                      @click="navigateTo(item.route)"
                      :aria-label="item.label"
                      :aria-current="isActiveRoute(item.route) ? 'page' : null">
                    <div v-if="isActiveRoute(item.route)" class="active-indicator"></div>
                    <div class="nav-item-content">
                      <component :is="item.icon" class="nav-icon" />
                      <span class="nav-text">{{ item.label }}</span>
                    </div>
                  </BButton>
                </template>
                {{ item.label }}
              </BPopover>
            </template>

            <!-- Wenn expanded, zeige Section Header und Items -->
            <template v-else>
              <BButton class="section-header"
                      :class="{ 'expanded': expandedSections[section.id] }"
                      @click="handleToggleSection(section.id)"
                      :aria-expanded="expandedSections[section.id]"
                      :aria-label="`${section.title ? $t(section.title) : section.titleVerbatim} Bereich umschalten`">
                <span class="section-title">{{ section.title ? $t(section.title) : section.titleVerbatim }}</span>
                <div class="chevron-icon" :class="{ 'rotated': expandedSections[section.id] }">
                  <ChevronDown class="icon" />
                </div>
              </BButton>

              <div v-show="expandedSections[section.id]" class="nav-items">
                <BButton class="nav-item"
                        v-for="item in section.items"
                        :key="item.id"
                        :disabled="item.disabled"
                        :aria-disabled="item.disabled ? 'true' : 'false'"
                        :class="{ active: isActiveRoute(item.route) }"
                        @click="navigateTo(item.route)"
                        :aria-current="isActiveRoute(item.route) ? 'page' : null">
                  <div v-if="isActiveRoute(item.route)" class="active-indicator"></div>
                  <div class="nav-item-content">
                    <component :is="item.icon" class="nav-icon" />
                    <span class="nav-text">{{ item.label ? $t(item.label) : item.labelVerbatim }}</span>
                  </div>
                </BButton>
              </div>

              <div class="section-separator" v-if="section !== navigationSections[navigationSections.length - 1]"></div>
            </template>
          </div>

        </div>


        <div class="section-separator my-4"></div>

        <div class="version-info  my-4" v-if="!sidebarCollapsed">
          <p class="app-name">
            <span><b>BOLD</b> Solution GmbH</span>
            <!-- MSM Loop Dashboard -->
          </p>
          <p class="version">
            <span v-if="appTarget === ''">MSM </span>Loop v{{ frontendVersion }}<sup>&copy;</sup>
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="sidebar-footer">
        <div class="collapse-button-wrapper">
          <BButton  class="collapse-button"
                    @click="toggleSidebarCollapsed"
                    :aria-label="sidebarCollapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'">
            <ChevronLeft v-if="!sidebarCollapsed" class="collapse-icon" />
            <ChevronRight v-else class="collapse-icon" />
          </BButton>
          <BButton ref="infoBtn"
                  class="collapse-button"
                  @click="showInfoPanel = !showInfoPanel"
                  :aria-label="$t('Webportal.Info')">
            <Info class="collapse-icon" />
          </BButton>
        </div>
      </div>
    </div>

    <AppInfoPanel  :show="showInfoPanel"
                   :anchor-el="$refs.infoBtn?.$el"
                   placement="top-right"
                   @close="showInfoPanel = false" />
  </nav>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Search, Target, Gauge, ChartColumn,
  Download, Database, Zap, ExternalLink,
  Files, Settings, PanelsTopLeft, ChevronDown, PackageOpen, PanelRightOpen,
  ChevronLeft, ChevronRight, DiamondPercent, Bot, LayoutDashboard, Info } from 'lucide-vue-next';
import AppInfoPanel from '@/componentsv3/AppInfoPanel.vue';

export default {
  name: 'AppSidebar',

  components: {
    Search, Target, Gauge, ChartColumn,
    Download, Database, Zap, ExternalLink, Info,
    Files, Settings, PanelsTopLeft, ChevronDown, PackageOpen, PanelRightOpen,
    ChevronLeft, ChevronRight, DiamondPercent, Bot, LayoutDashboard,
    AppInfoPanel
  },

  data() {
    return {
      desktopCollapsedState: null,
      showInfoPanel: false
    };
  },

  computed: {
    ...mapGetters('app', [
      'sidebarCollapsed',
      'navigationSections',
      'expandedSections',
      'isNavigationLoaded',
      'isSkewedTheme' // NEU
    ]),
    ...mapGetters('auth', ['logoUrl']),

    frontendVersion() {
      return FRONTEND_VERSION;
    },

    appTarget() {
      return APP_TARGET;
    },
  },

  mounted() {
    // Navigation beim Mount laden (falls noch nicht geladen)
    this.loadNavigation();

    // Initiale Prüfung
    this.handleResize();

    // Resize Listener hinzufügen
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    // Cleanup: Resize Listener entfernen
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    // Store Actions
    ...mapActions('app', [
      'toggleSidebarCollapsed',
      'setSidebarCollapsed',
      'loadNavigation',
      'toggleSection',
      'setMobileSidebar'
    ]),

    handleResize() {
      const isMobile = window.innerWidth < 991;

      if (isMobile) {
        // Mobile: collapsed State speichern und Sidebar auf 288px setzen
        if (this.sidebarCollapsed) {
          this.desktopCollapsedState = true;
          this.setSidebarCollapsed(false);
        }
      } else {
        // Desktop: gespeicherten State wiederherstellen
        if (this.desktopCollapsedState !== null) {
          this.setSidebarCollapsed(this.desktopCollapsedState);
          this.desktopCollapsedState = null;
        }
      }
    },

    handleToggleSection(sectionId) {
      this.toggleSection(sectionId);
    },

    isActiveRoute(route) {
      if (!route) return false;
      return this.$route.path === route || this.$route.path.startsWith(route + '/');
    },

    navigateTo(route) {
      if (!route || this.$route.path === route) return;

      // Auf Mobile (< 991px) die Sidebar nach Navigation schließen
      const isMobile = window.innerWidth < 991;
      if (isMobile) {
        this.setMobileSidebar(false);
      }

      this.$router.push(route);
    },
  }
};
</script>

<style lang="scss" scoped>
.app-sidebar {
  font-family: "BMWTypeNext Pro", sans-serif;
  position: fixed;
  font-weight: 500;
  top: 0;
  left: 0;
  z-index: 1030;
  width: 288px;
  height: 100vh;
  transition: width 150ms ease-out;

  // Collapsed State nur auf Desktop (>= 992px)
  @media (min-width: 992px) {
    &.collapsed {
      width: 90px;

      .app-sidebar-container {
        width: 90px;
      }

      .logo-image {
        width: 60px;
        height: 60px;
      }

      .logo-wrapper {
        width: 60px;
        height: 60px;
      }

      .navigation-content {
        padding: 48px 8px 24px;
      }

      .nav-item {
        justify-content: center;
        padding: 10px 8px;
      }

      .nav-text {
        display: none;
      }

      .section-separator {
        display: none;
      }
    }
  }

  // Auf Mobile immer volle Breite (288px)
  @media (max-width: 991px) {
    width: 288px !important;

    .app-sidebar-container {
      width: 288px !important;
    }
  }
}

.app-sidebar-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 300ms ease-out;
  position: relative;
  width: 288px;
  background-color: var(--navbar-bg);
}

/* Logo Section */
.logo-section {
  flex-shrink: 0;
  width: 100%;
  height: 144px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-wrapper {
  width: 225px;
  height: 112.5px;
  margin-top: 32px;
  margin-bottom: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 300ms ease-out;
}

.logo-image {
  width: 225px;
  height: 112.5px;
  object-fit: contain;
  border-radius: var(--radius-sm);
  transition: all 300ms ease-out;
}

/* Navigation Content */
.navigation-content {
  flex: 1;
  overflow-y: auto;
  padding: 48px 16px 24px;
  transition: padding 300ms ease-out;
}

.nav-sections {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Section Header */
.section-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  color: rgba(255, 255, 255, 0.6);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 200ms ease-out;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
    background-color: rgba(255, 255, 255, 0.05);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
}

.section-title {
  font-family: "BMWTypeNext Pro", sans-serif;
  font-size: 0.75em;
  font-weight: 300;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.chevron-icon {
  transition: transform 200ms ease-out;
  transform: rotate(0deg);

  &.rotated {
    transform: rotate(180deg);
  }

  .icon {
    width: 16px;
    height: 16px;
  }
}

/* Section Separator */
.section-separator {
  height: 1px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  margin: 16px 0;
  transition: all 300ms ease-out;
}

/* Navigation Items */
.nav-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 200ms ease-out;
  color: rgba(255, 255, 255, 0.8);

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }

  &:disabled {
    background-color: transparent !important;
    color: var(--bmw-disabled) !important;
    cursor: not-allowed;
    pointer-events: none;

    .nav-icon {
      color: var(--bmw-disabled) !important;
    }

    .nav-text {
      color: var(--bmw-disabled) !important;
    }

    &:hover {
      background-color: transparent !important;
    }
  }

  &.active {
    background-color: var(--navbar-item-active-bg);
    color: white;
  }
}

.active-indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background-color: white;
  border-radius: var(--radius-none) var(--radius-max) var(--radius-max) var(--radius-none);
}

.nav-item-content {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-text {
  font-weight: 500;
  white-space: nowrap;
  font-size: 14px;
  transition: opacity 300ms ease-out;
}

/* Sidebar Footer */
.sidebar-footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 991px) {
    display: none;
  }
}

.collapse-button-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;

}

.collapse-button {
  padding: 8px;
  border-radius: var(--radius-md);
  color: rgba(255, 255, 255, 0.8);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 200ms ease-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }

  .collapse-icon {
    width: 20px;
    height: 20px;
  }
}

.version-info {
  text-align: center;
  transition: opacity 300ms ease-out;

  .app-name {
    color: rgba(255, 255, 255, 0.4);
    font-size: 12px;
    font-weight: 500;
    margin: 0;
  }

  .version {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    font-family: monospace;
    margin: 0;
  }
}

/* ==========================================
   SKEWED THEME - Neues BMW Design
   ========================================== */
.app-sidebar.theme-skewed {
  .navigation-content {
    padding: 48px 32px 10.5px 4px
  }

  .nav-section {
    gap: 3.8px;
    margin-bottom: 22.5px;
    position: relative;
    z-index: 1;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      bottom: -22.5px;
      left: 0;
      right: 0;
      height: 2px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.05);
      transform: skewX(var(--target-skew-angle-inverse));
    }
  }

  .section-header {
    padding: 8.5px 12.25px 8.5px 22.25px;
    border-radius: var(--radius-xs);

    .section-title {
      font-size: 11.3px;
      letter-spacing: 0.563px;
      line-height: 15px;
    }

    .chevron-icon {
      .icon {
        width: 15px;
        height: 15px;
      }

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .nav-items {
    gap: 10px;
  }

  .nav-item {
    transform: skewX(var(--target-skew-angle-inverse));
    padding: 0;
    border-radius: var(--bs-border-radius-sm);
    width:90%;
    margin-left: 1rem;
    position: relative;
    z-index: auto;

    .nav-item-content {
      transform: skewX(var(--target-skew-angle)) scaleY(1.1247);
      padding: 12px;
      gap: 4px;
    }

    .active-indicator {
      left: 0;
      width: 3px;
      border-radius: var(--radius-none) var(--radius-max) var(--radius-max) var(--radius-none);
    }

    .nav-icon {
      width: 18.75px;
      height: 18.75px;
    }

    .nav-text {
      font-size: 14px;
      line-height: 16px;
    }

    &.active {
      border-left-width: 3px;
    }
  }

  .section-separator {
    display: none;
  }
}

// Collapsed State für Skewed Theme - nur auf Desktop
@media (min-width: 992px) {
  .app-sidebar.theme-skewed.collapsed {
    .nav-section::after {
      display: none;
    }

    // Popover braucht höheren z-index im collapsed State
    :deep(.popover) {
      z-index: 1080 !important;
    }
  }
}
</style>
<style lang="scss" scoped>
 .app-sidebar, nav, .navbar {
  background-color: var(--navbar-bg) !important;
  color: var(--navbar-color);
}

.nav-link {
  color: var(--navbar-color);

  &:hover {
    color: var(--navbar-hover-color);
  }

  &.active {
    color: var(--navbar-active-color);
  }
}

</style>

<style lang="scss">
.popover {
  z-index: 1070 !important; // Höher als sidebar (1030)
}

.b-popover {
  z-index: 1070 !important;
}
</style>
